import { List, UpdateVersionModel, VersionModel } from '@partful/instructions-domain-models';
import { useTenant } from '@providers/Tenant';
import { InfiniteData, QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { instructionQueryKeys } from '../../shared/api/queryKeys';
import { VersionsService } from '../api/versionsService';

export function useUpdateInstructionVersion() {
  const queryClient = useQueryClient();
  const { accountId } = useTenant();
  const service = useMemo(() => new VersionsService(), []);

  return useMutation<
    VersionModel,
    unknown,
    { instructionId: string; versionId: string; updateArgs: UpdateVersionModel }
  >({
    mutationFn: ({ instructionId, versionId, updateArgs }) =>
      service.update(accountId, instructionId, versionId, updateArgs),
    onSuccess: (data, { instructionId }) => {
      updateInstructionVersionInCache(queryClient, data, instructionId);
    },
  });
}

function updateInstructionVersionInCache(queryClient: QueryClient, version: VersionModel, instructionId: string) {
  queryClient.setQueryData<VersionModel>(
    [instructionQueryKeys.getInstructionVersion, instructionId, version.id],
    version
  );
  queryClient.setQueryData<InfiniteData<List<VersionModel>>>(
    [instructionQueryKeys.listInstructionVersions, instructionId],
    (oldData) => {
      if (oldData) {
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            items: page.items.map((cacheVersion) => {
              if (cacheVersion.id === version.id) {
                return version;
              }

              if (version.status === 'published' && cacheVersion.status === 'published') {
                return {
                  ...cacheVersion,
                  status: 'draft',
                  __version: cacheVersion.__version + 1,
                };
              }

              return cacheVersion;
            }),
          })),
        };
      }
    }
  );
}
